/**
 * B1 · হোম — `/`
 * Frame: `Oprappo P0 Screens.dc.html`, "B1 · হোম".
 *
 * Frame note: "All eight labels hold on one line at 360. The teal header band gives the
 * search field a surface to sit on; the amber left rule on the live order is the
 * screen's single amber element."
 *
 * No advertising banner, one live order, one repeat row — the flow-B note is explicit
 * that this is the whole screen and that the test is whether it stays calm.
 *
 * ── WIRED. WHAT CHANGED, AND WHY IT MATTERED ────────────────────────────────
 * This screen greeted every person who opened the app as রফিকুল ইসলাম and showed them a
 * technician on the way to an address they had never given, plus two bills they had never
 * paid. All three were constants in `./data.ts`. They are gone.
 *
 *   the name          `GET /v1/account/profile` → `profile.name_bn`
 *   the live order    `GET /v1/activity?tab=active`, then `GET /v1/activity/:id`
 *   the repeat cards  `GET /v1/home/repeats` — the slot; see `./api.ts`
 *
 * What is left in `data.ts` is chrome: the eight quick-action labels, the greeting, the
 * placeholder and the two section headings, which are the same sentence for everybody.
 *
 * ── A VISITOR WITH NO ACCOUNT GETS A COHERENT SCREEN ────────────────────────
 * Search is reachable before an account exists (docs/05 § search), so the screen in front
 * of it has to be too. A signed-out visitor's three reads are refused with `E_PERMISSION`,
 * `./api.ts § emptyOnRefusal` absorbs exactly that code, and B1 renders: the greeting with
 * no name under it, the eight actions, and neither of the two blocks that are about orders
 * this person does not have. Nothing is invented to fill the space, and no state frame is
 * shown — not being signed in is not a failure of the home screen.
 *
 * Every OTHER failure still reaches its drawn state through the boundary: an outage is
 * `E_OFFLINE`, which is S6, drawn on this very screen.
 *
 * The header search field is a display control, not an input: tapping it opens `/search`,
 * which is the one door. It is drawn 50px with a `#5B6B69` magnifier and `padding:0 13px`;
 * § 02's `Search` is 52px with a `#8B9795` magnifier, `padding:0 14px` and a recents
 * panel. Transcribed from B1 and the difference reported rather than reconciled.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, IconButton, TabBar } from '@oprappo/ui';
import { formatTaka } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../onboarding/frame.js';
import { Glyph } from '../onboarding/glyphs.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { relativeTimeBn } from '../../lib/relative-time.js';
import { fetchHome, type HomeData, type HomeRepeat } from './api.js';
import { HOME_HEADER, LIVE_ORDER, QUICK_ACTIONS, REPEAT, type Mark } from './data.js';

function QuickMark({ mark }: { mark: Mark }): JSX.Element {
  return mark.from === 'icon' ? (
    <Icon name={mark.name} size={24} stroke="#0E3A45" />
  ) : (
    <Glyph name={mark.name} size={24} stroke="#0E3A45" />
  );
}

/**
 * The second line of a repeat card.
 *
 * The server's `detail_bn` is already localised and is rendered verbatim. `relativeTimeBn`
 * is reached only when the server sent NO line — which is the case the drawn card is a
 * relative time all on its own (`গত মাস`) — and it returns null rather than a sentence
 * when the gap is older than the six drawn phrases. Nothing here composes Bangla out of
 * two halves: it picks one whole string or renders no second line at all.
 */
function repeatDetailBn(card: HomeRepeat, now: Date): string | null {
  if (card.detail_bn !== '') return card.detail_bn;
  return card.updated_at === null ? null : relativeTimeBn(card.updated_at, now);
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the three reads returned and renders it; the container below does
 * the loading. The split is what lets the fidelity test render this screen from the same
 * fixtures the visual gate serves, so the chain is checked end to end:
 * drawn frame == fixture == API output == this markup.
 *
 * `null` means "nothing arrived", and the honest rendering of nothing is less screen —
 * never a substituted design value.
 *
 * `now` is passed IN rather than read here. See `lib/relative-time.ts`: a component that
 * reads the clock cannot be screenshotted twice and compared, and the 2% gate does exactly
 * that.
 */
export function HomeView({ data, now }: { data: HomeData | null; now: Date }): JSX.Element {
  const navigate = useNavigate();
  const live = data?.live ?? null;
  const repeats = data?.repeats ?? [];

  return (
    <Frame className={SAND_FRAME}>
      {/* `background:#0E3A45; padding:16px 16px 18px` */}
      <div className="bg-teal px-[16px] pt-[16px] pb-[18px]">
        <div className="flex justify-between items-center mb-[14px]">
          <div>
            <div className="font-anek font-normal text-[13px] leading-[1.75] text-on-teal-muted">
              {HOME_HEADER.greeting_bn}
            </div>
            {/*
              No name, no line. A placeholder here — a dash, a spinner, `অতিথি` — would be
              this file writing Bangla about a person it knows nothing about, and the frame
              draws a name rather than a name slot.
            */}
            {data?.name_bn === null || data?.name_bn === undefined ? null : (
              <div className="font-anek font-semibold text-[17px] leading-[1.75] text-white mt-[1px]">
                {data.name_bn}
              </div>
            )}
          </div>
          {/*
            `border:1px solid rgba(255,255,255,.32)` — an alpha of white, not a palette
            colour, so it stays an arbitrary value.
          */}
          <div className="w-[34px] h-[34px] border border-[rgba(255,255,255,.32)] rounded-full flex items-center justify-center">
            <IconButton control="bell" count={0} size={17} stroke="#ffffff" />
          </div>
        </div>

        <div className="bg-white h-[50px] flex items-center px-[13px] gap-[11px]">
          <Icon name="search" size={18} stroke="#5B6B69" className="flex-none" />
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 rounded-none font-anek font-normal text-[16px] leading-[1.75] text-muted"
          >
            {HOME_HEADER.placeholder_bn}
          </button>
          <IconButton
            control="voiceSearch"
            size={18}
            onClick={() => navigate('/search/voice')}
            className="flex-none"
          />
          <IconButton control="imageSearch" size={18} className="flex-none" />
        </div>
      </div>

      <div className="px-[16px] pt-[16px] flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-4 gap-x-[8px] gap-y-[12px]">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={a.to === undefined ? undefined : () => navigate(a.to as string)}
              className="text-center bg-transparent border-0 p-0 rounded-none"
            >
              <div className="h-[42px] flex items-center justify-center">
                <QuickMark mark={a.mark} />
              </div>
              <div
                className={
                  a.emphasis === true
                    ? 'font-anek font-semibold text-[13px] leading-[1.75] text-teal'
                    : 'font-anek font-medium text-[13px] leading-[1.75] text-ink2'
                }
              >
                {a.label_bn}
              </div>
            </button>
          ))}
        </div>

        {/*
          NO LIVE ORDER, NO BLOCK — heading included. `চলমান` over nothing is a heading
          that promises a card, and the frame draws no empty variant of this block; the
          drawn empty home is its own frame (`P0 States § ১`), which the boundary reaches
          through `?state=new_user`.
        */}
        {live === null ? null : (
          <div className="mt-[22px]">
            <div className="font-anek font-semibold text-[17px] leading-[1.75] text-ink mb-[10px]">
              {LIVE_ORDER.heading_bn}
            </div>
            {/*
              `Status` in @oprappo/ui is § 05's card: no amber left rule, no chevron, a
              `#E2DED5` progress track, and its detail sits below the bar rather than above
              it. B1 draws all four differently, so this is transcribed. Reported.
            */}
            <button
              type="button"
              onClick={() =>
                navigate(data?.live_id === null || data?.live_id === undefined ? '/activity' : `/activity/${data.live_id}`)
              }
              className="w-full text-left bg-white border border-border border-l-[3px] border-l-amber px-[14px] py-[13px] rounded-none"
            >
              <div className="flex justify-between items-start gap-[11px]">
                <div>
                  <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                    {live.title_bn}
                  </div>
                  <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mt-[2px]">
                    {live.detail_bn}
                  </div>
                </div>
                <Glyph name="rowChevron" size={17} stroke="#5B6B69" className="flex-none mt-[4px]" />
              </div>
              <div
                className="h-[4px] bg-sand-deep mt-[12px] flex"
                role="progressbar"
                aria-valuenow={live.percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {/*
                  INLINE, AND IT HAS TO STAY INLINE — a live order's progress, any value
                  0–100, now a real number from `GET /v1/activity/:id`. See
                  `apps/web/public/_headers` § style-src.
                */}
                <div className="bg-teal" style={{ width: `${String(live.percent)}%` }} />
              </div>
            </button>
          </div>
        )}

        {repeats.length === 0 ? null : (
          <div className="mt-[20px]">
            <div className="flex justify-between items-baseline mb-[10px]">
              <div className="font-anek font-semibold text-[17px] leading-[1.75] text-ink">
                {REPEAT.heading_bn}
              </div>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                {REPEAT.hint_bn}
              </div>
            </div>
            <div className="flex gap-[10px]">
              {repeats.map((c) => {
                const detail = repeatDetailBn(c, now);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={c.to === null ? undefined : () => navigate(c.to as string)}
                    className="flex-1 min-w-0 text-left bg-white border border-border p-[12px] rounded-none"
                  >
                    <div className="font-anek font-medium text-[14px] leading-[1.75] text-ink">
                      {c.title_bn}
                    </div>
                    {detail === null ? null : (
                      <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[2px]">
                        {detail}
                      </div>
                    )}
                    {/* Latin numerals: Manrope, tabular figures, exactly as drawn. */}
                    <div className="font-manrope font-semibold text-[16px] leading-[1] text-ink [font-variant-numeric:tabular-nums] mt-[9px]">
                      {formatTaka(c.amount_minor)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <TabBar
        activeKey="home"
        onChange={(key) => {
          if (key === 'explore') navigate('/explore');
          if (key === 'ai') navigate('/ai');
          if (key === 'activity') navigate('/activity');
          if (key === 'account') navigate('/account');
        }}
      />
    </Frame>
  );
}

export function HomeScreen(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchHome(signal), []);
  const { data, error } = useResource(load, 'home');

  // Read once, at the top of the render, and handed down. Not `new Date()` inside the
  // card: two cards rendered a millisecond apart could then straddle a minute boundary
  // and disagree about what `এখন` means on the same screen.
  const now = new Date();

  return (
    <SurfaceStates surface="discovery" error={error ?? undefined}>
      <HomeView data={data} now={now} />
    </SurfaceStates>
  );
}
